import React from 'react';

interface PopoverReason {
  reason: string;
  severity?: string;
  reasonId?: string;
  dismissed?: boolean;
}

interface PopoverProps {
  reasons: PopoverReason[];
  position: { x: number; y: number };
  onClose?: () => void;
  onDismissAll?: () => void;
  popoverRef?:
  | React.RefObject<HTMLDivElement>
  | React.RefObject<HTMLDivElement | null>
  | React.MutableRefObject<HTMLDivElement | null>;
  onToggleReason?: (reasonId: string) => void;
  showDismissed?: boolean;
  onToggleShowDismissed?: () => void;
}

const Popover: React.FC<PopoverProps> = ({
  reasons,
  position,
  onClose,
  onDismissAll,
  popoverRef,
  onToggleReason,
  showDismissed = false,
  onToggleShowDismissed,
}) => {
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: 100,
  };

  const safeReasons = Array.isArray(reasons) ? reasons : [];
  const dismissedCount = safeReasons.filter(r => r.dismissed).length;
  const hasActiveReasons = safeReasons.some(r => !r.dismissed);
  const visibleReasons = showDismissed ? safeReasons : safeReasons.filter(r => !r.dismissed);

  const getSeverityClass = (severity?: string) => {
    if (severity === 'red') return 'text-red-300';
    if (severity === 'orange') return 'text-orange-300';
    return 'text-gray-200';
  };

  return (
    <div
      ref={popoverRef}
      style={style}
      className="bg-gray-900 text-white text-xs rounded shadow-lg p-3 max-w-sm border border-gray-700 space-y-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">Conflicts</span>
        <div className="flex items-center gap-2">
          {onDismissAll && hasActiveReasons && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDismissAll(); }}
              className="text-[11px] px-2 py-1 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700"
            >
              Dismiss all
            </button>
          )}
          {dismissedCount > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleShowDismissed?.(); }}
              className="text-[11px] px-2 py-1 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700"
            >
              {showDismissed ? 'Hide dismissed' : `Show dismissed (${dismissedCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            aria-label="Close conflict popover"
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>
      </div>

      {visibleReasons.length > 0 ? (
        <ul className="list-disc pl-4 space-y-1">
          {visibleReasons.map((reasonObj, index) => (
            <li
              key={reasonObj.reasonId || index}
              className={`${getSeverityClass(reasonObj.severity)} flex items-start justify-between gap-2`}
            >
              <span className={`${reasonObj.dismissed ? 'line-through text-gray-400' : ''}`}>
                {reasonObj.reason}
              </span>
              {onToggleReason && reasonObj.reasonId && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleReason(reasonObj.reasonId!); }}
                  className="text-[11px] px-2 py-0.5 rounded border border-gray-600 hover:bg-gray-800 whitespace-nowrap"
                >
                  {reasonObj.dismissed ? 'Restore' : 'Dismiss'}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-gray-400">
          {dismissedCount > 0 && !showDismissed
            ? 'No active conflicts. Show dismissed to view them.'
            : 'No conflicts to display.'}
        </div>
      )}
    </div>
  );
};

export default Popover;
